import React, { useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Chip, Text, useSwitchChipGroup, SwitchChipGroup } from '@frontend/design-system';

type Option = { id: string; label: string };

export type CallIntelSwitchChipGroupProps = {
  options: Option[];
  value: string[];
  allLabel?: React.ReactNode;
  trackingPrefix?: string;
  onChange: (next: string[]) => void;
};

const ALL_STATUS_KEY = 'all';

export function CallIntelSwitchChipGroup({
  options,
  value,
  onChange,
  allLabel,
  trackingPrefix = 'status-filter-button',
  ...rest
}: CallIntelSwitchChipGroupProps) {
  const { t } = useTranslation('analytics');

  const allOptionIds = useMemo(() => options.map((o) => o.id), [options]);

  // if `value` empty or undefined, treat as "all" selected
  const effectiveSelection = value != null && value.length > 0 ? value : allOptionIds;

  const initialGroupValue = useMemo(
    () => (effectiveSelection.length === allOptionIds.length ? [ALL_STATUS_KEY] : effectiveSelection),
    [allOptionIds.length, effectiveSelection]
  );

  const { value: groupValue, setValue: setGroupValue } = useSwitchChipGroup({
    defaultValue: initialGroupValue,
  });

  const allActive = groupValue.includes(ALL_STATUS_KEY);

  useEffect(() => {
    const nextValue = effectiveSelection.length === allOptionIds.length ? [ALL_STATUS_KEY] : effectiveSelection;
    setGroupValue(nextValue);
  }, [effectiveSelection, allOptionIds.length]);

  const handleSelectAll = useCallback(() => {
    if (!allActive) {
      onChange(allOptionIds);
      setGroupValue([ALL_STATUS_KEY]);
    }
  }, [allActive, allOptionIds, onChange]);

  const handleGroupValueChange = useCallback<typeof setGroupValue>(
    (next) => {
      const nextValue = typeof next === 'function' ? next(groupValue) : next;
      // remove any stray 'all' key and invalid IDs
      const cleaned = nextValue.filter((v) => v !== ALL_STATUS_KEY && allOptionIds.includes(v));
      if (cleaned.length === allOptionIds.length) {
        // user selected all options
        onChange(allOptionIds);
        setGroupValue([ALL_STATUS_KEY]);
      } else {
        onChange(cleaned);
        setGroupValue(cleaned);
      }
    },
    [allOptionIds, groupValue, onChange, setGroupValue]
  );

  return (
    <div css={styles.wrapper} {...rest}>
      <Chip
        css={styles.allChip(allActive)}
        onClick={handleSelectAll}
        size='large'
        variant={allActive ? 'primaryDark' : 'outline'}
        trackingId={`${trackingPrefix}-"all`}
      >
        <Text size='medium' color={allActive ? 'white' : 'default'}>
          {t('All') || allLabel}
        </Text>
      </Chip>

      <Divider css={styles.divider} />

      <div css={styles.switchGroupWrapper}>
        <SwitchChipGroup value={groupValue} setValue={handleGroupValueChange} checkboxMode>
          {options.map(({ id, label }) => {
            return (
              <SwitchChipGroup.Option
                className={
                  // only highlight individual chips when NOT "All"
                  !allActive && groupValue.includes(id) ? 'selected-chip' : ''
                }
                key={id}
                value={id}
                trackingId={`${trackingPrefix}-${label?.toLocaleLowerCase()}`}
                isNew
              >
                <Text size='medium'>{label}</Text>
              </SwitchChipGroup.Option>
            );
          })}
        </SwitchChipGroup>
      </div>
    </div>
  );
}

const styles = {
  wrapper: css`
    display: flex;
    gap: ${theme.spacing(1)};
    align-items: center;
  `,
  allChip: (selected: boolean) => css`
    cursor: pointer;
    p {
      color: ${theme.colors.neutral90};
    }
    ${selected &&
    `
      background-color: ${theme.colors.primary5};
      border: 1px solid ${theme.colors.primary50};
    `}
  `,
  divider: css`
    border-left: 1px solid ${theme.colors.neutral20};
    height: ${theme.spacing(5)};
  `,
  switchGroupWrapper: () => css`
    .selected-chip {
      border: 1px solid ${theme.colors.primary50};
    }
  `,
};
